/** Human-readable labels for every scorecard metric key. */
export const SCORECARD_DISPLAY_NAME: Record<string, string> = {
  prs_merged_30d: 'PRs merged (30d)',
  ttv_avg_seconds_30d: 'Avg TTV (30d)',
  auto_approval_rate_30d: 'Auto-approval rate (30d)',
  revert_rate_30d: 'Revert rate (7d on bot-approved)',
  override_rate_30d: 'Override rate (/request-jedi-review)',
  ttfr_avg_seconds_30d: 'Avg TTFR (30d)',
  findings_acted_on_rate_30d: 'Findings acted-on (30d)',
  avg_cost_per_pr_30d: 'Avg cost per PR (30d)',
  tokens_in_per_day: 'Council tokens / day',
  cache_read_share_30d: 'Cache-read share (30d)',
};

/**
 * Per-metric hover-tooltip text. Surfaces on a small (i) glyph next to the
 * scorecard tile title via the browser's native title= tooltip. Kept short —
 * longer concept writeups live on /help.
 */
export const SCORECARD_METRIC_HELP: Record<string, string> = {
  prs_merged_30d: 'Number of PRs merged in the last 30 days. Bars show daily counts over the 90-day window.',
  auto_approval_rate_30d: 'Of all unique PRs Diff Vader reviewed in the last 30 days (live mode), the share whose final verdict was APPROVE — i.e. cleared without requiring a human reviewer. Each PR counts once; a PR that went REQUEST_CHANGES then APPROVE still counts. NEEDS_HUMAN and TOO_LARGE PRs stay in the denominator. Dashed line marks the 35% target. Sparkline shows artifact-level APPROVE% (each DV run counted separately); the tile counts each PR once by its final verdict.',
  ttv_avg_seconds_30d: 'Average time-to-verdict (mean of daily p50s): from PR ready-for-review to Diff Vader posting its verdict, averaged over the last 30 days.',
  revert_rate_30d: 'Of bot-approved PRs that merged in the last 30 days, percent that were later reverted within 7 days. PRs merged in the last ~7 days may not yet have their settlement data populated.',
  override_rate_30d: 'Of unique PRs Diff Vader reviewed in the last 30 days, percent where a human ran /request-jedi-review to request a human reviewer.',
  ttfr_avg_seconds_30d: 'Average time-to-first-review (mean of daily p50s): from PR ready-for-review to first human review comment.',
  findings_acted_on_rate_30d: 'Of substantive DV review comments in the last 30 days, percent whose anchored line was modified before merge (GitHub reports position=null). Early-rollout numbers will look low — this measures forward-only behavior change, not quality.',
  avg_cost_per_pr_30d: 'Average Diff Vader spend per unique PR reviewed in live mode in the last 30 days. Sums cost_usd across all council runs for the PR (including reruns); divides by the same unique-PR count the Auto-approval card uses. Lower is better. Target line is a stretch goal — current mean is dragged by a long tail of expensive PRs. Sparkline shows daily average over the 30-day window.',
  tokens_in_per_day: 'Total council input tokens per day (input + cache reads + cache writes). Watch this drop after prompt caching rolls out.',
  cache_read_share_30d: 'Of council input tokens on caching-enabled (SDK-path) runs in the last 30 days, share served from cache (cache reads are excluded from the per-minute quota and cost ~10%). Runs predating the caching rollout are excluded from the denominator so they don’t dilute the share. Baseline is 0% — no caching existed before the rollout.',
};

/** Formats a ratio as a percentage with 1 decimal place (0.05 → "5.0%"). */
export function fmtPercent(v: number | null): string {
  if (v === null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

/** Formats USD with two decimals ($1.23). Null/NaN/Infinity → em dash. */
export function fmtUsd(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—';
  return `$${v.toFixed(2)}`;
}

/** Formats a large count compactly (21,416,331 → "21.4M") so big token totals
 *  fit a metric tile (baseline / Last-30d / delta pill) without overflowing.
 *  Null/NaN/Infinity → em dash. */
export function fmtCompact(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}

/** Formats a duration in seconds as "Xh Ym" / "Xm Ys" / "Xs". */
export function fmtSeconds(v: number | null): string {
  if (v === null || !Number.isFinite(v) || v < 0) return '—';
  const total = Math.round(v);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const PERCENT_METRICS = new Set([
  'auto_approval_rate_30d',
  'revert_rate_30d',
  'override_rate_30d',
  'findings_acted_on_rate_30d',
  'cache_read_share_30d',
]);

const DURATION_SECONDS_METRICS = new Set([
  'ttv_avg_seconds_30d',
  'ttfr_avg_seconds_30d',
]);

const CURRENCY_METRICS = new Set([
  'avg_cost_per_pr_30d',
]);

// Large counts shown compactly so they fit a tile (token totals run into the
// tens of millions; the raw number overflowed the card + delta pill).
const COMPACT_METRICS = new Set([
  'tokens_in_per_day',
]);

/** Returns the appropriate formatter for a scorecard metric key, or undefined for plain numeric. */
export function metricFormat(metric: string): ((v: number | null) => string) | undefined {
  if (PERCENT_METRICS.has(metric)) return fmtPercent;
  if (DURATION_SECONDS_METRICS.has(metric)) return fmtSeconds;
  if (CURRENCY_METRICS.has(metric)) return fmtUsd;
  if (COMPACT_METRICS.has(metric)) return fmtCompact;
  return undefined;
}
